/**
 * The quest editor and the map editor run in separate tabs. The map editor
 * announces a saved route; the quest tab refetches. BroadcastChannel first, a
 * storage event as the fallback, and the quest tab also refetches on focus.
 */
const CHANNEL = "mapus-quest";
const STORAGE_KEY = "mapus.quest.routeSaved";

export interface RouteSavedMessage {
  type: "route-saved";
  questId: string;
  at: number;
}

/** Window name so pressing Edit again focuses the same editor tab. */
export function questRouteWindowName(questId: string): string {
  return `mapus-route-${questId}`;
}

export function announceRouteSaved(questId: string): void {
  const msg: RouteSavedMessage = { type: "route-saved", questId, at: Date.now() };
  try {
    const ch = new BroadcastChannel(CHANNEL);
    ch.postMessage(msg);
    ch.close();
  } catch {
    /* BroadcastChannel unavailable */
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(msg));
  } catch {
    /* storage unavailable */
  }
}

function parseRouteSavedMessage(data: unknown): RouteSavedMessage | null {
  const msg = data as Partial<RouteSavedMessage> | null;
  if (msg?.type !== "route-saved" || typeof msg.questId !== "string") return null;
  return { type: "route-saved", questId: msg.questId, at: typeof msg.at === "number" ? msg.at : 0 };
}

function listenRouteSaved(handler: (msg: RouteSavedMessage) => void): () => void {
  const accept = (data: unknown) => {
    const msg = parseRouteSavedMessage(data);
    if (msg) handler(msg);
  };
  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(CHANNEL);
    ch.onmessage = (e) => accept(e.data);
  } catch {
    ch = null;
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      accept(JSON.parse(e.newValue));
    } catch {
      /* ignore */
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    ch?.close();
    window.removeEventListener("storage", onStorage);
  };
}

export function onRouteSaved(questId: string, handler: () => void): () => void {
  return listenRouteSaved((msg) => {
    if (msg.questId === questId) handler();
  });
}

/** Home map and other tabs: refresh when any quest route was saved in the editor. */
export function onAnyRouteSaved(handler: (questId: string) => void): () => void {
  return listenRouteSaved((msg) => handler(msg.questId));
}

const BLOCK_PREFIX = "mapus.quest.mapBlock.";
const ROUTE_PIECE_PREFIX = "mapus.quest.routePiece.";

/** Last editor Piece id saved for this quest draft when bind has not succeeded yet. */
export function readQuestRoutePieceId(questId: string): string | null {
  try {
    const raw = localStorage.getItem(ROUTE_PIECE_PREFIX + questId);
    return raw && /^[0-9a-f-]{36}$/i.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeQuestRoutePieceId(questId: string, pieceId: string): void {
  try {
    localStorage.setItem(ROUTE_PIECE_PREFIX + questId, pieceId);
  } catch {
    /* storage unavailable */
  }
}

export function clearQuestRoutePieceId(questId: string): void {
  try {
    localStorage.removeItem(ROUTE_PIECE_PREFIX + questId);
  } catch {
    /* storage unavailable */
  }
}

/** The creator pressed Add route: the page shows an (empty) map block. */
export function readMapBlock(questId: string): boolean {
  try {
    return localStorage.getItem(BLOCK_PREFIX + questId) === "1";
  } catch {
    return false;
  }
}

export function writeMapBlock(questId: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(BLOCK_PREFIX + questId, "1");
    else localStorage.removeItem(BLOCK_PREFIX + questId);
  } catch {
    /* storage unavailable */
  }
}
